import React from 'react';
import { FileCheck } from 'lucide-react';
import Badge from '@components/common/Badge';
import { Assessment, TableColumn } from '@app-types';
import { RZTable } from '@components/common/table';
import { StatusCell, AssessorCell } from '@components/common/table';
import { hasOpenConflicts, getTierConflictTooltip } from '@utils/conflictUtils';
import { statusToVariant } from '@utils/badgeMappers';
import { getAssessmentStatusLabel } from '@utils/referenceCatalog';
import { formatCalendarDate, formatInstantAsDate } from '@utils/dateUtils';
import { InfoCard, ProgressBar } from '@components/common';
import { LoadingState } from '@components/common/loading';

interface ContractAssessmentsProps {
  assessments: Assessment[];
  isLoading?: boolean;
  onViewAssessment?: (assessmentId: number) => void;
  /** Program Lead sees general info only, without process progress (ADO-1176) */
  hideProgress?: boolean;
}

// Column configuration
const contractAssessmentColumns: TableColumn[] = [
  { key: 'assessment_id', label: 'Assessment #', defaultVisible: true, alwaysVisible: true },
  { key: 'name', label: 'Name', defaultVisible: true },
  { key: 'assessor', label: 'Assessor', defaultVisible: true },
  { key: 'scheduled_date', label: 'Scheduled Date', defaultVisible: true },
  { key: 'assessment_date', label: 'Assessment Date', defaultVisible: true },
  { key: 'assessment_year', label: 'Assessment Year', defaultVisible: true },
  { key: 'progress', label: 'Progress', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true }
];

const ContractAssessments: React.FC<ContractAssessmentsProps> = ({
  assessments,
  isLoading = false,
  onViewAssessment,
  hideProgress = false
}) => {
  const columns = hideProgress
    ? contractAssessmentColumns.filter((column) => column.key !== 'progress')
    : contractAssessmentColumns;

  // Row class function for conflict highlighting
  const getRowClassName = (assessment: Assessment): string => {
    return hasOpenConflicts(assessment) ? 'has-conflict' : '';
  };

  // Cell rendering logic
  const renderCell = (column: TableColumn, assessment: Assessment) => {
    switch (column.key) {
      case 'assessment_id':
        return (
          <button className="table-link" onClick={() => onViewAssessment?.(assessment.assessment_id)}>
            AS-{assessment.assessment_id.toString().padStart(6, '0')}
          </button>
        );

      case 'name':
        return <div>{assessment.assessment_name}</div>;

      case 'status':
        return (
          <StatusCell
            hasConflict={hasOpenConflicts(assessment)}
            conflictTooltip={hasOpenConflicts(assessment) ? getTierConflictTooltip(assessment) : undefined}
          >
            <Badge variant={statusToVariant(assessment.assessment_status)}>{getAssessmentStatusLabel(assessment.assessment_status)}</Badge>
          </StatusCell>
        );

      case 'assessor':
        return <AssessorCell assessorName={assessment.assessor_name} assessorEmail={assessment.assessor_email} />;

      case 'scheduled_date':
        return formatInstantAsDate(assessment.scheduled_date, 'Not scheduled');

      case 'assessment_date':
        return formatCalendarDate(assessment.completed_date, '-');

      case 'assessment_year':
        return assessment.assessment_year ?? '-';

      case 'progress':
        return <ProgressBar percentage={assessment.progress_percentage} />;

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <InfoCard>
        <InfoCard.Header>
          <InfoCard.Title icon={<FileCheck size={18} />}>Assessments</InfoCard.Title>
        </InfoCard.Header>
        <InfoCard.Content>
          <LoadingState message="Loading assessments..." />
        </InfoCard.Content>
      </InfoCard>
    );
  }

  return (
    <InfoCard>
      <InfoCard.Header>
        <InfoCard.Title icon={<FileCheck size={18} />}>Assessments ({assessments.length})</InfoCard.Title>
      </InfoCard.Header>

      <InfoCard.Content>
        <RZTable
          data={assessments}
          columns={columns}
          getRowKey={(assessment) => assessment.assessment_id}
          renderCell={renderCell}
          rowClassName={getRowClassName}
          state={{
            isLoading: false,
            isEmpty: assessments.length === 0,
            emptyTitle: 'No assessments found',
            emptyDescription: 'No assessments found for this contract.',
            emptyType: 'default'
          }}
        />
      </InfoCard.Content>
    </InfoCard>
  );
};

export default ContractAssessments;
